import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { formatAppVersion } from "../core/app-version";
import { AppUpdateService } from "../core/pwa/app-update.service";
import { GoogleAuthService } from "../services/google-api.service";
import { setReturnUrl } from "../services/return-url.utils";
import { LoaderService } from "../shared/services/loader.service";
import { LOGIN_SESSION_UPGRADE_QUERY } from "src/session.config";

export const LOGIN_OAUTH_ERROR_MESSAGES: Record<string, string> = {
	oauth_state: "Sign-in expired or was interrupted. Please try again.",
	oauth_missing: "Sign-in could not be completed. Please try again.",
	no_refresh_token: "Google did not complete sign-in. Please try again and allow access if prompted.",
	user_not_found: "Your Google account is not registered for this app. Contact an administrator.",
	google_denied: "Google sign-in was cancelled. Please try again.",
	oauth_failed: "Sign-in failed. Please try again.",
};

export function messageForLoginErrorCode(code: string | null): string | undefined {
	if (!code) {
		return undefined;
	}
	return LOGIN_OAUTH_ERROR_MESSAGES[code] ?? LOGIN_OAUTH_ERROR_MESSAGES["oauth_failed"];
}

@Component({
	selector: "app-login",
	templateUrl: "./login.component.html",
	styleUrls: ["./login.component.scss"],
	changeDetection: ChangeDetectionStrategy.Eager,
	standalone: false,
})
export class LoginComponent implements OnInit, OnDestroy {
	private isCheckingForUpdates = false;
	private latestStatusTimeoutId: ReturnType<typeof setTimeout> | undefined;

	updateCheckStatus: "idle" | "checking" | "latest" = "idle";
	readonly appVersion = formatAppVersion();

	readonly horizonTicks = Array.from({ length: 9 }, (_, i) => i);
	readonly speedTapeValues = Array.from({ length: 40 }, (_, i) => 280 + i * 2);
	readonly altitudeTapeValues = Array.from({ length: 40 }, (_, i) => 34000 + i * 100);
	readonly compassTicks = Array.from({ length: 36 }, (_, i) => i);
	readonly compassDirs = ["N", "E", "S", "W"];
	readonly dataStreamLines = [
		"› AUTH HANDSHAKE INITIATED",
		"› TLS 1.3 · CIPHER OK",
		"› NAV DB SYNC · 100%",
		"› TELEMETRY LINK · UP",
		"› AWAITING CREDENTIALS",
	];
	readonly borderLights: Array<{
		top: string;
		side: "left" | "right";
		color: string;
		delay: string;
	}> = [
		{ top: "8%", side: "left", color: "#E94335", delay: "0s" },
		{ top: "32%", side: "left", color: "#3b82f6", delay: "0.7s" },
		{ top: "62%", side: "left", color: "#E94335", delay: "1.4s" },
		{ top: "88%", side: "left", color: "#3b82f6", delay: "0.3s" },
		{ top: "8%", side: "right", color: "#3b82f6", delay: "1.1s" },
		{ top: "32%", side: "right", color: "#E94335", delay: "0.4s" },
		{ top: "62%", side: "right", color: "#3b82f6", delay: "1.8s" },
		{ top: "88%", side: "right", color: "#E94335", delay: "0.9s" },
	];

	constructor(
		private loadingService: LoaderService,
		public readonly googleAuthService: GoogleAuthService,
		private appUpdateService: AppUpdateService,
		private route: ActivatedRoute,
		private router: Router,
	) {}

	ngOnInit(): void {
		const returnUrl = this.route.snapshot.queryParamMap.get("returnUrl");
		if (returnUrl) {
			setReturnUrl(returnUrl);
		}

		const authed = this.route.snapshot.queryParamMap.get("authed");
		if (authed === "1") {
			void this.googleAuthService.completePostLoginFlow();
			return;
		}

		const loginError = messageForLoginErrorCode(this.route.snapshot.queryParamMap.get("error"));
		if (loginError) {
			this.googleAuthService.loginError = loginError;
			void this.router.navigate([], {
				relativeTo: this.route,
				queryParams: { error: null },
				queryParamsHandling: "merge",
				replaceUrl: true,
			});
		}

		void this.googleAuthService.configure();
		this.applyPendingAppUpdateIfNeeded();
	}

	private applyPendingAppUpdateIfNeeded(): void {
		const isSessionUpgrade =
			this.route.snapshot.queryParamMap.get(LOGIN_SESSION_UPGRADE_QUERY) === "1";

		if (isSessionUpgrade) {
			this.loadingService.setLoading(true);
			void this.appUpdateService.applySessionUpgradeUpdate().finally(() => {
				this.loadingService.setLoading(false);
			});
			return;
		}

		void this.appUpdateService.applyUpdateIfReady();
	}

	ngOnDestroy(): void {
		if (this.latestStatusTimeoutId !== undefined) {
			clearTimeout(this.latestStatusTimeoutId);
		}
	}

	googleSignInClicked(): void {
		this.loadingService.setLoading(true);
		try {
			this.googleAuthService.signInToGoogle();
		} finally {
			this.loadingService.setLoading(false);
		}
	}

	async checkForUpdates(): Promise<void> {
		if (this.isCheckingForUpdates) {
			return;
		}

		this.isCheckingForUpdates = true;
		this.updateCheckStatus = "checking";
		this.loadingService.setLoading(true);

		try {
			await new Promise((resolve) => setTimeout(resolve, 0));
			const result = await this.appUpdateService.manualCheck();
			if (result === "up-to-date") {
				this.updateCheckStatus = "latest";
				if (this.latestStatusTimeoutId !== undefined) {
					clearTimeout(this.latestStatusTimeoutId);
				}
				this.latestStatusTimeoutId = setTimeout(() => {
					this.updateCheckStatus = "idle";
					this.latestStatusTimeoutId = undefined;
				}, 4000);
			} else {
				this.updateCheckStatus = "idle";
			}
		} catch (error) {
			console.error("Error checking for updates:", error);
			this.updateCheckStatus = "idle";
		} finally {
			this.isCheckingForUpdates = false;
			this.loadingService.setLoading(false);
		}
	}
}
